import { useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet } from 'react-router-dom';
import { useSession } from '../../auth/client.ts';
import { TeacherShell } from './Layout.tsx';
import { upsertTeacher } from './api.ts';

interface TabSpec {
  to: string;
  label: string;
}

const TABS: TabSpec[] = [
  { to: '/teacher/dashboard/lessons', label: 'Authoring' },
  { to: '/teacher/dashboard/live', label: 'Live Session' },
  { to: '/teacher/dashboard/review', label: 'Review' },
];

/**
 * Dashboard wrapper.
 *
 * Gates on useSession (redirects unauthenticated users to /teacher/sign-in)
 * then fires off /api/school/teacher/upsert exactly once per mount so the
 * school.teacher row is always materialised by the time the dashboard
 * renders. Treats upsert failure as non-blocking — the placeholder UI
 * still works; the real failure surface is handled when the founder
 * wires actual teacher-scoped queries.
 */
export function TeacherDashboard(): JSX.Element {
  const { data: session, isPending } = useSession();
  const [teacherName, setTeacherName] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    upsertTeacher()
      .then((t) => {
        if (!cancelled) setTeacherName(t.displayName);
      })
      .catch(() => {
        // Non-blocking: dashboard placeholder still renders.
      });
    return () => {
      cancelled = true;
    };
  }, [session]);

  if (isPending) {
    return (
      <TeacherShell>
        <div className="grid h-[60vh] place-items-center text-slate-500">Loading…</div>
      </TeacherShell>
    );
  }

  if (!session) {
    return <Navigate to="/teacher/sign-in" replace />;
  }

  return (
    <TeacherShell
      trailingNav={
        <span className="text-sm text-slate-600">
          {teacherName ? `Signed in as ${teacherName}` : 'Signed in'}
        </span>
      }
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <nav aria-label="Dashboard sections" className="flex gap-1 border-b border-amber-100 pt-4">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) =>
                [
                  'rounded-t-xl px-4 py-3 text-sm font-semibold transition',
                  'min-h-[48px] min-w-[48px]',
                  isActive
                    ? 'bg-white text-amber-700 shadow-[0_-2px_0_inset_var(--color-explorers-primary)]'
                    : 'text-slate-600 hover:bg-amber-50 hover:text-slate-900',
                ].join(' ')
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>

        <section className="mt-6 pb-12">
          <Outlet />
        </section>
      </div>
    </TeacherShell>
  );
}
